import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Page, Field } from "../components/Primitives";
import { toast } from "sonner";
import { Plus, Trash2, Settings as SettingsIcon } from "lucide-react";

const SCOPES = [
  { key: "store", label: "Stores / Parts Categories", hint: "Used in Stores Inventory to classify items." },
  { key: "warranty", label: "Warranty Categories", hint: "Used when recording a warranty." },
  { key: "service", label: "Service Categories", hint: "For job card tagging." },
  { key: "lead", label: "Lead Sources", hint: "How a customer reached you." },
];

export default function Settings() {
  const [tab, setTab] = useState("store");
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const load = async () => {
    const { data } = await api.get("/settings/categories", { params: { scope: tab } });
    setCats(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/settings/categories", { name: name.trim(), scope: tab, description: desc || null });
      toast.success("Category added");
      setName(""); setDesc(""); load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/settings/categories/${id}`);
    toast.success("Deleted"); load();
  };

  const current = SCOPES.find((s) => s.key === tab);

  return (
    <Page
      number="12"
      title="Settings"
      subtitle="Curate dropdown categories used across Stores, Warranty, Service and Leads."
      right={<SettingsIcon size={18} className="text-[var(--kk-muted)]" />}
    >
      <div className="kk-tabs mb-6">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            onClick={() => setTab(s.key)}
            className={"kk-tab " + (tab === s.key ? "kk-tab-active" : "")}
            data-testid={`settings-tab-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <form onSubmit={add} className="lg:col-span-1 kk-card p-5 h-fit">
          <div className="kk-overline">Add new</div>
          <div className="font-display text-lg uppercase mt-1">{current?.label}</div>
          <p className="text-xs text-[var(--kk-muted)] mt-1">{current?.hint}</p>
          <div className="mt-4 space-y-4">
            <Field label="Name *">
              <input className="kk-input" required value={name} onChange={(e) => setName(e.target.value)} data-testid="cat-name" />
            </Field>
            <Field label="Description">
              <input className="kk-input" value={desc} onChange={(e) => setDesc(e.target.value)} />
            </Field>
          </div>
          <button type="submit" className="kk-btn-primary mt-5" data-testid="add-category">
            <Plus size={14} /> Add
          </button>
        </form>

        <div className="lg:col-span-2">
          <div className="kk-overline mb-3">{cats.length} categories</div>
          <ul className="divide-y divide-[var(--kk-border)] border-t border-b border-[var(--kk-border)]">
            {cats.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3" data-testid={`cat-row-${c.id}`}>
                <div>
                  <div className="font-semibold">{c.name}</div>
                  {c.description && <div className="text-xs text-[var(--kk-muted)]">{c.description}</div>}
                </div>
                <button onClick={() => remove(c.id)} className="kk-pill kk-pill-muted" data-testid={`del-cat-${c.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </li>
            ))}
            {cats.length === 0 && <li className="py-6 text-sm text-[var(--kk-muted)] text-center">No categories yet — add one from the left.</li>}
          </ul>
        </div>
      </div>
    </Page>
  );
}
